import React, { useContext, useCallback } from 'react';
import { IEditorProps } from './Editor';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { EditorLabel } from './EditorLabel';
import { ListBox } from 'components/lists/ListBox';
import { EditorConfigButtons } from './EditorConfigButtons';
import { EditorConfigButton } from './EditorConfigButton';
import { IPropertyChild, getChildValueAsArray } from 'components/property-editors/utils/ProviderNodeUtils';
import { IListItemProps } from 'components/lists/ListItem';
import { EntityPickedInfo } from './entity-picker/EditorEntityPicker';
import { getEditorSettings } from 'components/property-editors/utils/EditorConfigurationUtils';
import { EditorValidationWrapper } from './EditorValidationWrapper';

interface IMultipleEntityPickerEditorProps extends IEditorProps {}

/**
 * A Multiple Entity Picker editor, which launches a picker dialog for pick Entity.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function MultipleEntityPickerEditor({ child, path, onChange }: IMultipleEntityPickerEditorProps) {
  const context = useContext(PropertyEditorContext);

  const onPicked = useCallback(
    (pickedInfo: EntityPickedInfo) => {
      const current = getChildValueAsArray(child);
      const name = pickedInfo?.name;
      if (name && !current.includes(name)) {
        onChange([...current, name], path);
      }
    },
    [child, onChange, path]
  );

  const onShowEditor = useCallback(() => {
    context.onShowDialogue({
      node: child,
      path,
      editingContextId: context.editingContextId,
      options: {
        entityPickerSettings: getEditorSettings(child),
        onPicked,
      },
    });
  }, [child, context, onPicked, path]);

  const onDrop = useCallback(
    (index: number) => {
      const value = [...getChildValueAsArray(child)];
      value.splice(index, 1);
      onChange(value, path);
    },
    [child, onChange, path]
  );

  const onClear = useCallback(() => {
    onChange([], path);
  }, [onChange, path]);

  return (
    <EditorLabel editor={child} path={path} doNotWrap className="dpc-multiple-picker-editor">
      <EditorValidationWrapper editor={child}>
        <ListBox items={getListItems(child)} onRemove={onDrop} noItemsText=" " />
      </EditorValidationWrapper>
      <EditorConfigButtons>
        <EditorConfigButton onClick={onClear}>Clear All</EditorConfigButton>
        <EditorConfigButton onClick={onShowEditor}>Add New</EditorConfigButton>
      </EditorConfigButtons>
    </EditorLabel>
  );
}

function getListItems(child: IPropertyChild): IListItemProps[] {
  return getChildValueAsArray(child).map(
    (label, index) =>
      ({
        label,
        index,
      } as IListItemProps)
  );
}
